import { useState } from 'react';
import type { ReactNode, FormEvent, JSX } from 'react';
import { useAuth } from './AuthProvider';
import { cn } from '@/lib/utils';
import { LoginScreen } from './LoginScreen';

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps): JSX.Element {
  const { status, signInWithPassword, authError, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (status === 'loading') {
    return (
      <FullScreenState title="Caricamento sessione" message="Verifico lo stato dell'accesso..." />
    );
  }

  // In local mode (no Supabase), bypass auth and allow access
  if (status === 'unsupported') {
    return <>{children}</>;
  }

  if (status !== 'authenticated') {
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!email || !password) return;
      setIsSubmitting(true);
      try {
        await signInWithPassword(email.trim(), password);
      } catch (error) {
        console.error('[AuthGate] sign-in failed', error);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <LoginScreen
        email={email}
        password={password}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        errorMessage={authError}
        onClearError={clearError}
      />
    );
  }

  return <>{children}</>;
}

function FullScreenState({
  title,
  message,
  tone = 'muted',
}: {
  title: string;
  message: string;
  tone?: 'muted' | 'warning';
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-secondary px-6 text-center">
      <div
        className={cn(
          'rounded-2xl border px-6 py-5 shadow-md',
          tone === 'warning'
            ? 'border-warning-500 bg-warning-100 text-brand-blue-800'
            : 'border-border-default bg-surface text-text-secondary'
        )}
      >
        <p className="text-base font-semibold">{title}</p>
        <p className="text-sm mt-1 max-w-md">{message}</p>
      </div>
    </div>
  );
}
